<template>
  <div class="app-container">
    <div>
      <el-select
        v-model="selectedDomain"
        class="search-select"
        type="select"
        style="margin-bottom: 10px"
        :placeholder="$t('common.domain')"
        @input="handleDomainChange($event)"
      >
        <el-option v-for="domain in domains" :key="domain.id" :label="domain.name" :value="domain.id" />
      </el-select>
      <el-select
        v-model="workshopIds"
        class="search-select"
        type="select"
        :multiple="true"
        :multiple-limit="workshopsLimit"
        style="margin-left: 4px;margin-bottom: 10px"
        :placeholder="$t('common.workshop')"
        @input="refreshList($event)"
      >
        <el-option v-for="workshop in workshops" :key="workshop.id" :label="workshop.name" :value="workshop.id" />
      </el-select>

      <el-select
        v-model="shipmentListTypeID"
        class="search-select"
        type="select"
        style="margin-left: 4px;margin-bottom: 10px"
        :placeholder="$t('transportOrder.shipmentListTypeName')"
        @input="refreshList($event)"
      >
        <el-option
          v-for="shipmentListType in transportCompanyShipmentListTypes"
          :key="shipmentListType.id"
          :label="shipmentListType.name"
          :value="shipmentListType.id"
        />
      </el-select>
      <el-date-picker
        v-model="shipmentAt"
        width="50"
        type="datetime"
        style="margin-left: 4px"
        :format="dateTimeFormat"
        :placeholder="$t('transportOrder.shipmentAt')"
        name="shipmentAt"
        :picker-options="pickerOptions"
      />
      <el-select
        v-model="storeAttributeID"
        class="search-select"
        type="select"
        clearable
        style="margin-left: 4px;margin-bottom: 10px"
        :placeholder="$t('transportOrder.storeAttribute')"
        @input="refreshList($event)"
      >
        <el-option v-for="attr in storeAttributes" :key="attr.id" :label="attr.name" :value="attr.id" />
      </el-select>
      <el-select
        v-model="truckID"
        class="search-select"
        type="select"
        clearable
        style="margin-left: 4px;margin-bottom: 10px"
        :placeholder="$t('transportOrder.truckIdentifier')"
      >
        <el-option v-for="attr in trucks" :key="attr.id" :label="attr.name" :value="attr.id" />
      </el-select>
      <div style="float:right;">
        <editor-button
          :icon-only="false"
          :loading="creatingShipments"
          button-translation="transportOrder.createShipments"
          action="primary"
          :disabled="!canCreateShipments"
          @click="createShipments()"
        />
      </div>
      <el-collapse-transition>
        <el-alert v-if="errorMsg" :title="errorMsg" type="error" :closable="false" />
      </el-collapse-transition>
    </div>
    <div ref="tableContainer">
      <pagination
        :page-sizes="[50, 100]"
        :total="loadedData.total"
        :page.sync="page"
        :limit.sync="pageSize"
        @pagination="refreshList"
      >
      </pagination>
      <el-table
        ref="transportSelectionList"
        :data="filteredItems"
        border
        row-key="orderLineID"
        fit
        :max-height="getMaxTableHeight()"
        class="el-transport-table"
        @selection-change="handleSelectionChange"
      >
        <el-table-column label-class-name="filter-cell">
          <template slot="header" slot-scope="{}">
            <el-input
              v-model="search['storeCode']"
              class="search"
              size="mini"
              :placeholder="$t('transportOrder.storeCode')"
              clearable
              prefix-icon="el-icon-search"
            />
          </template>
          <el-table-column :label="$t('transportOrder.storeCode')" prop="storeCode" width="120px">
            <template slot-scope="scope">
              <span>{{ scope.row['storeCode'] }}</span>
            </template>
          </el-table-column>
        </el-table-column>
        <el-table-column label-class-name="filter-cell">
          <template slot="header" slot-scope="{}">
            <el-input
              v-model="search['storeName']"
              class="search"
              size="mini"
              :placeholder="$t('orderLine.storeName')"
              clearable
              prefix-icon="el-icon-search"
            />
          </template>
          <el-table-column :label="$t('orderLine.storeName')" prop="storeName">
            <template slot-scope="scope">
              <span>{{ scope.row['storeName'] }}</span>
            </template>
          </el-table-column>
        </el-table-column>
        <el-table-column>
          <el-table-column :label="$t('transportOrder.shipmentListTypeName')" prop="storeName">
            <template slot-scope="scope">
              <span>{{ scope.row['shipmentListTypeName'] }}</span>
            </template>
          </el-table-column>
        </el-table-column>
        <el-table-column label-class-name="filter-cell">
          <template slot="header" slot-scope="{}">
            <el-input
              v-model="search['seriesNumber']"
              class="search"
              size="mini"
              :placeholder="$t('transportOrder.seriesNumber')"
              clearable
              prefix-icon="el-icon-search"
            />
          </template>
          <el-table-column :label="$t('transportOrder.seriesNumber')" prop="seriesNumber" width="120px">
            <template slot-scope="scope">
              <span>{{ scope.row['seriesNumber'] }}</span>
            </template>
          </el-table-column>
        </el-table-column>
        <el-table-column>
          <template slot="header" slot-scope="{}">
            <el-select v-model="search['workshopID']" class="search-select" size="mini" clearable>
              <el-option
                v-for="workshop in selectedWorkshops"
                :key="workshop.id"
                :label="workshop.name"
                :value="workshop.id"
              />
            </el-select>
          </template>
          <el-table-column :label="$t('common.workshop')" width="150px">
            <template slot-scope="scope">
              <span>{{ scope.row['workshopName'] }}</span>
            </template>
          </el-table-column>
        </el-table-column>
        <el-table-column label-class-name="filter-cell">
          <template slot="header" slot-scope="{}">
            <el-input
              v-model="search['ticketNumber']"
              class="search"
              size="mini"
              :placeholder="$t('transportOrder.ticketNumber')"
              clearable
              prefix-icon="el-icon-search"
            />
          </template>
          <el-table-column :label="$t('transportOrder.ticketNumber')" prop="ticketNumber" width="140px">
            <template slot-scope="scope">
              <span>{{ scope.row['ticketNumber'] }}</span>
            </template>
          </el-table-column>
        </el-table-column>
        <el-table-column label-class-name="filter-cell">
          <template slot="header" slot-scope="{}">
            <el-input
              v-model="search['consumerName']"
              class="search"
              size="mini"
              :placeholder="$t('orderLine.consumerName')"
              clearable
              prefix-icon="el-icon-search"
            />
          </template>
          <el-table-column :label="$t('orderLine.consumerName')" prop="consumerName">
            <template slot-scope="scope">
              <span>{{ scope.row['consumerName'] }}</span>
            </template>
          </el-table-column>
        </el-table-column>
        <el-table-column>
          <template slot="header" slot-scope="{}">
            <el-select v-model="search['sampleYN']" class="search-select" size="mini" clearable>
              <el-option
                v-for="option in [
                  { value: true, label: $t('common.yes') },
                  { value: false, label: $t('common.no') }
                ]"
                :key="option.value"
                :label="option.label"
                :value="option.value"
              />
            </el-select>
          </template>
          <el-table-column :label="$t('orderLine.show')" width="150px">
            <template slot-scope="scope">
              <span>{{ scope.row['sampleYN'] ? $t('common.yes') : $t('common.no') }}</span>
            </template>
          </el-table-column>
        </el-table-column>
        <el-table-column>
          <el-table-column :label="$t('transportOrder.parcels')" width="120px">
            <template slot-scope="scope">
              <span>{{ scope.row['parcels'] }}</span>
            </template>
          </el-table-column>
        </el-table-column>
        <el-table-column>
          <el-table-column :label="$t('transportOrder.status')" width="120px">
            <template slot-scope="scope">
              <span>{{ formatOrderLineStatus(scope.row, scope.column, scope.row['status']) }}</span>
            </template>
          </el-table-column>
        </el-table-column>
        <el-table-column>
          <el-table-column :label="$t('transportOrder.subStatus')" width="120px">
            <template slot-scope="scope">
              <span>{{ scope.row['subStatus'] }}</span>
            </template>
          </el-table-column>
        </el-table-column>
        <el-table-column label-class-name="filter-cell">
          <template slot="header" slot-scope="{}">
            <el-date-picker
              v-model="search['scannedAt']"
              type="date"
              :format="dateFormat"
              :value-format="dateFormat"
              :placeholder="$t('transportOrder.scannedAt')"
              clearable
            />
          </template>
          <el-table-column :label="$t('transportOrder.scannedAt')">
            <template slot-scope="scope">
              <span>{{ formatDate(scope.row['scannedAt']) }}</span>
            </template>
          </el-table-column>
        </el-table-column>
        <el-table-column
          fixed="right"
          type="selection"
          :reserve-selection="true"
          width="40"
          align="center"
          :selectable="() => true"
        >
        </el-table-column>
        <el-table-column>
          <el-table-column fixed="right" width="180">
            <template slot-scope="scope">
              <el-button
                v-if="!isExcluded(scope.row)"
                type="text"
                size="small"
                @click.native.prevent="excludeRow(scope.row)"
              >
                {{ $t('transportOrder.isIncluded') }}
              </el-button>
              <el-button
                v-if="isExcluded(scope.row)"
                type="text"
                size="small"
                @click.native.prevent="includeRow(scope.row)"
              >
                {{ $t('transportOrder.isExcluded') }}
              </el-button>
            </template>
          </el-table-column>
        </el-table-column>
      </el-table>
    </div>
    <div style="margin-top: 10px; text-align:center">
      <label class="selectionFooterLabel">{{ $t('transportOrder.planningDateRange') }}: </label>
      <el-date-picker
        v-model="preselectionRange"
        :readonly="true"
        type="daterange"
        format="dd-MM-yyyy HH:mm"
        value-format="yyyy-MM-ddTHH:mm:ss"
      >
      </el-date-picker>
    </div>
  </div>
</template>

<script>
import EditorButton from '@/components/crud/EditorButton';
import Pagination from '@/components/Pagination';
import { toISOString } from '@/utils/date';
import { toDateTimeString } from '@/utils/date';
import { uniqueArr } from '@/utils';

const debounce = require('lodash.debounce');

export default {
  name: 'Planning',
  components: {
    EditorButton,
    Pagination
  },
  data() {
    const workshops = this.$store.getters.workshops;

    return {
      loading: false,
      loadedData: { items: [], total: 0 },
      filteredItems: [],
      search: {},
      errorMsg: '',
      workshopIds: workshops && workshops.length === 1 ? [workshops[0].id] : [],
      domainID: null,
      storeAttributeID: null,
      shipmentListTypes: [],
      shipmentListTypeID: null,
      transportCompanyID: null,
      truckID: null,
      shipmentAt: null,
      page: 1,
      pageSize: 50,
      multipleSelection: [],
      excluded: [],
      preselectionRange: [],
      creatingShipments: false
    };
  },
  computed: {
    dateFormat() {
      return this.$store.getters.dateFormat;
    },
    listParams() {
      return {
        workshopIds: this.getFilteredWorkshops(),
        domainID: this.selectedDomain,
        storeAttributeID: +this.storeAttributeID > 0 ? this.storeAttributeID : null,
        shipmentListTypeID: this.shipmentListTypeID,
        page: this.page,
        pageSize: this.pageSize,
        storeCode: this.search.storeCode,
        storeName: this.search.storeName,
        seriesNumber: this.search.seriesNumber,
        ticketNumber: this.search.ticketNumber,
        consumerName: this.search.consumerName,
        sampleYN: this.search.sampleYN,
        scannedAt: this.search.scannedAt
      };
    },
    items() {
      return this.loadedData.items;
    },
    selectedDomain: {
      get() {
        return this.domainID || this.$store.getters['domain/getDefaultSelectValueDomainID'];
      },
      set(newDomain) {
        return (this.domainID = newDomain);
      }
    },
    dateTimeFormat() {
      return this.$store.getters.dateTimeFormat;
    },
    selectedWorkshops() {
      const selectedWorkshopIds = this.workshopIds;
      return this.workshops.filter(w => selectedWorkshopIds.some(id => id === w.id));
    },
    workshops() {
      return this.$store.getters.workshops;
    },
    workshopsLimit() {
      const domains = this.domains;
      if (domains) {
        const domainID = this.selectedDomain;
        if (domainID) {
          const selectedDomain = domains.find(domain => domain.id === domainID);
          return selectedDomain && selectedDomain.transportOrderMultipleWorkshopsYN ? 0 : 1; // 0 means no limit.
        }
      }
      return 1;
    },
    storeAttributes() {
      return this.$store.getters['storeAttribute/loadedItems'].filter(e => e.domainID === this.selectedDomain);
    },
    domains() {
      return this.$store.getters.domains;
    },
    transportCompanyShipmentListTypes() {
      const transportCompanyID = this.transportCompanyID;
      return transportCompanyID > 0
        ? this.shipmentListTypes.filter(item => item.transportCompanyID === transportCompanyID)
        : [];
    },
    trucks() {
      return this.$store.getters['truck/loadedItems'];
    },
    truckName() {
      const truck = this.$store.getters['truck/getLoadedItemByID'](this.truckID);
      return truck && truck.name;
    },
    canCreateShipments() {
      return (
        this.selectedDomain &&
        this.workshopIds &&
        this.workshopIds.length > 0 &&
        this.shipmentListTypeID &&
        this.shipmentAt
      );
    },
    pickerOptions() {
      return {
        disabledDate(givenDate) {
          const currentDate = new Date();
          currentDate.setHours(0, 0, 0, 0);
          return new Date(givenDate) < currentDate - 1;
        },
        firstDayOfWeek: 1
      };
    }
  },
  watch: {
    search: {
      deep: true,
      handler: function(value) {
        this.listParams.page = 1;
        this.debouncedUpdateSearch();
      }
    }
  },
  created() {
    const domains = this.$store.getters.domains;
    const domainID = this.selectedDomain;
    if (domainID && domains) {
      const domain = domains.find(item => item.id === domainID);
      this.transportCompanyID = domain && domain.transportCompanyID;
    }
    this.$store.dispatch('storeAttribute/getItems');
    this.$store.dispatch('shipmentListType/getItems').then(data => {
      if (data && data.items) {
        this.shipmentListTypes = data.items;
      } else {
        this.shipmentListTypes = [];
      }
    });
    this.$store.dispatch('truck/getItems');
    this.debouncedUpdateSearch = debounce(this.refreshList, 500);
  },
  methods: {
    getFilteredWorkshops() {
      const workshopID = this.search.workshopID;
      if (workshopID) {
        return this.workshopIds.some(id => id === workshopID) ? [workshopID] : [0];
      }
      return this.workshopIds;
    },
    handleSelectionChange(val) {
      this.multipleSelection = val;
      const notExcluded = this.items
        .filter(e => !this.multipleSelection.some(selected => selected.orderLineID === e.orderLineID))
        .map(ms => ms.orderLineID);

      const excluded = this.items
        .filter(e => this.multipleSelection.some(selected => selected.orderLineID === e.orderLineID))
        .map(ms => ms.orderLineID);

      this.excluded = this.excluded.filter(excludedID => !notExcluded.some(selectedID => selectedID === excludedID));
      this.excluded = uniqueArr(this.excluded.concat(excluded));
    },
    createShipments() {
      this.creatingShipments = true;
      const params = this.listParams;
      params.workshopIds = this.workshopIds; // When creating the shipments, this must include all selected workshops, not just the filtered one!
      this.$store
        .dispatch('transportOrder/createShipments', {
          ...params,
          truckIdentifier: this.truckName,
          shipmentAt: toISOString(this.shipmentAt),
          excludedOrderLines: this.excluded
        })
        .then(data => {
          this.$store.dispatch('notify/success', { message: this.$i18n.t('transportOrder.shipmentsCreated') });
        })
        .finally(() => {
          this.creatingShipments = false;
          this.refreshList();
        });
    },
    refreshList() {
      this.loading = true;
      const params = {
        ...this.listParams
      };
      params.workshopIds = params.workshopIds && params.workshopIds.join(',');
      this.$store
        .dispatch('transportOrder/getScannedOrders', params)
        .then(data => {
          this.filteredItems = data.items;
          this.loadedData.items = data.items;
          this.loadedData.total = data.total;
          const dates = data.items.filter(i => !!i.scannedAt).map(i => new Date(i.scannedAt));
          if (dates && dates.length) {
            const min = Math.min.apply(null, dates);
            const max = Math.max.apply(null, dates);
            const dateMin = min && new Date(min);
            const dateMax = max && new Date(max);
            this.preselectionRange = dateMin !== null && dateMax != null ? [dateMin, dateMax] : [];
          } else {
            this.preselectionRange = [];
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },
    reset() {
      this.loadedData.items = [];
      this.loadedData.total = 0;
      this.filteredItems = [];
      this.preselectionRange = [];
    },
    handleDomainChange(domainID) {
      const domain = this.domains.find(d => d.id === domainID);
      this.transportCompanyID = domain && domain.transportCompanyID;
      this.shipmentListTypeID = null;
      this.workshopIds = [];
      this.reset();
    },
    formatOrderLineStatus(row, column, cellValue) {
      const option =
        cellValue !== null &&
        this.$store.getters['option/loadedOptions']('orderLineStatus').find(item => item.code === cellValue);
      return option && option.description;
    },
    getMaxTableHeight() {
      if (this.$refs.tableContainer) {
        return window.innerHeight - this.$refs.tableContainer.getBoundingClientRect().top - 40;
      }
    },
    formatDate(value) {
      return toDateTimeString(value, 'DD-MM-YYYY HH:mm');
    },
    excludeRow(row) {
      this.$refs.transportSelectionList.toggleRowSelection(row, true);
    },
    includeRow(row) {
      this.$refs.transportSelectionList.toggleRowSelection(row, false);
    },
    isExcluded(row) {
      return this.excluded.some(id => id === row.orderLineID);
    }
  }
};
</script>

<style lang="scss">
.selectionFooterLabel {
  background-color: white;
  display: block;
  padding-bottom: 8px;
  font-size: 14px !important;
}

.el-transport-table .cell,
.el-table th div {
  padding-right: 0 !important;
  padding-left: 0px !important;
}
</style>
